import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  TableRow,
  TableCell,
  Grid,
  IconButton,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import { Form } from "@components";
import { EmployeeDelegateItem } from "@api/types/employeeswithdelegates";
import {
  deleteEmployeeDelegate,
  putEmployeeDelegate,
} from "@api/admin/employeedelegates";
import { alertService } from "@services";
import Hooks from "@hooks";

interface Props {
  delegate: EmployeeDelegateItem;
  resetTable: () => void;
}

type Key = keyof EmployeeDelegateItem;

export const DelegatesItem: React.FC<Props> = ({ delegate, resetTable }) => {
  const formDelegate = Form.useManager<EmployeeDelegateItem>({
    id: 0,
    userEmail: "",
    delegateEmail: "",
    isActive: false,
  });
  const [
    directory_delegateEmails,
    ,
    directory_delegateEmails_fetching,
  ] = Hooks.useDirectory(formDelegate.values.delegateEmail);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    resetForm();
  }, [delegate]);

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "delegateEmail":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleChange(name: Key, value: any): void {
    formDelegate.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      formDelegate.set(Form.target.ERROR, [name, error]);
    });
  }

  let DelegateItem: EmployeeDelegateItem = {
    id: 0,
    userEmail: delegate.userEmail,
    delegateEmail: "",
    isActive: false,
  };

  function resetForm() {
    formDelegate.values.id = delegate.id;
    formDelegate.values.userEmail = delegate.userEmail;
    formDelegate.values.delegateEmail = delegate.delegateEmail;
    formDelegate.values.isActive = delegate.isActive;
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(formDelegate.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          if (
            formDelegate.values.delegateEmail != delegate.delegateEmail ||
            formDelegate.values.isActive != delegate.isActive
          ) {
            sendPayload();
          } else {
            setIsEditing(false);
            resetForm();
          }
        } else {
          validations.forEach(([name, error]) => {
            formDelegate.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [formDelegate]);

  function sendPayload() {
    setIsSubmitting(true);
    DelegateItem.id = delegate.id;
    DelegateItem.userEmail = delegate.userEmail;
    DelegateItem.delegateEmail = formDelegate.values.delegateEmail;
    DelegateItem.isActive = formDelegate.values.isActive;
    putEmployeeDelegate(delegate.id, DelegateItem)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Delegate successfully updated." });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({
            msg:
              "Please check if delegate already exists for this user and try again.",
          });
        }
      })
      .then(() => {
        setIsEditing(false);
        resetTable();
        //resetForm();
        setIsSubmitting(false);
      });
    //setIsEditing(false);
  }

  const deleteEmployee = () => {
    setIsDeleting(true);
    deleteEmployeeDelegate(delegate.id)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Delegate successfully deleted." });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({ statusCode: error.response.status });
        }
      })
      .then(() => {
        resetTable();
        setIsDeleting(false);
      });
  };

  // for autocompletion
  function handleDelegateEmailInput(name: string, value: string): void {
    formDelegate.set(Form.target.VALUE, ["delegateEmail", value]);
  }

  function getDirectoryOptionsDelegateEmail(): string[] {
    return directory_delegateEmails
      ? directory_delegateEmails.map((user) => user.email)
      : [];
  }

  return (
    <TableRow style={{ height: "47px" }}>
      {isEditing ? (
        <React.Fragment>
          <TableCell align="center">
            <Grid container>
              <Form.Autocomplete
                name="delegateEmail"
                size={12}
                placeholder="email"
                value={formDelegate.values.delegateEmail}
                error={formDelegate.errors.delegateEmail}
                disabled={isSubmitting}
                onChange={handleChange}
                onInput={handleDelegateEmailInput}
                options={getDirectoryOptionsDelegateEmail()}
                loading={directory_delegateEmails_fetching}
              />
            </Grid>
          </TableCell>
          <TableCell align="center">
            <Grid container>
              <Form.Checkbox
                name="isActive"
                label="Delegate is active"
                size={12}
                value={formDelegate.values.isActive}
                disabled={isSubmitting}
                onChange={handleChange}
              />
            </Grid>
          </TableCell>
          <TableCell align="center">
            <Button
              disabled={isSubmitting}
              size="small"
              variant="outlined"
              onClick={handleSubmit}
              style={{ minWidth: "35px", maxWidth: "35px" }}
            >
              {isSubmitting ? <CircularProgress size={15} /> : "OK"}
            </Button>
          </TableCell>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <TableCell align="center">
            <Grid container>
              <Grid item>{delegate.delegateEmail}</Grid>
            </Grid>
          </TableCell>
          <TableCell align="center">
            <Grid container>
              <Grid item xs={12}>
                {delegate.isActive ? "Active" : "Inactive"}
              </Grid>
            </Grid>
          </TableCell>
          <TableCell align="center">
            <IconButton
              size="small"
              disabled={isDeleting}
              onClick={() => {
                setIsEditing(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </React.Fragment>
      )}
      <TableCell align="center">
        <IconButton
          size="small"
          onClick={deleteEmployee}
          disabled={isSubmitting || isDeleting}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
